import { Static, Type } from '@sinclair/typebox';

/**
 * Schémas admin « impayés abonnement » (dunning) : liste des pros avec chaînes de charge
 * en échec + rejeu manuel. cf. billing.admin.controller (backend) et la fiche pro admin.
 */

export const AdminOutstandingProSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.Union([Type.String(), Type.Null()]),
  subscription_status: Type.String(),
  payment_at_risk: Type.Boolean(),
  /** ISO — null si le pro n'est pas (plus) en cycle de dunning (ex. PAST_DUE hérité). */
  dunning_started_at: Type.Union([Type.String(), Type.Null()]),
  /** Même date, pré-formatée en locale/fuseau ops (Date Handling Rule) — affichage admin. */
  dunning_started_at_formatted: Type.Optional(Type.String()),
  /** Phase dérivée : RECOVERY | SEMI_FROZEN | FROZEN (PAST_DUE) — affichage admin. */
  dunning_phase: Type.Union([Type.String(), Type.Null()]),
  /** Nombre de chaînes de charge ouvertes (renewal + prorata). */
  open_chains: Type.Number(),
  /** Solde impayé formaté (somme des dernières tentatives FAILED). */
  outstanding_amount: Type.String(),
  /** Code d'erreur Stripe de la dernière tentative renewal (decline_code si dispo). */
  last_error_code: Type.Union([Type.String(), Type.Null()]),
  /** ISO de la dernière tentative en échec. */
  last_failed_at: Type.Union([Type.String(), Type.Null()]),
  last_failed_at_formatted: Type.Optional(Type.String()),
  /** true si le dernier échec est un hard decline (retries planifiés stoppés). */
  is_hard_decline: Type.Boolean(),
  /**
   * ISO — recouvrement automatique arrêté par un admin : plus de relance, plus de rejeu
   * planifié, plus de gel J+60. Le solde reste dû (chaînes ouvertes conservées).
   */
  dunning_abandoned_at: Type.Union([Type.String(), Type.Null()]),
  dunning_abandoned_at_formatted: Type.Optional(Type.String()),
  /**
   * Régularisations EN COURS : chaînes dont la dernière tentative est un prélèvement SEPA lancé, ni
   * encaissé ni rejeté (rejeu, « Payer maintenant », représentation). Hors du solde en échec et
   * jamais rejouées : l'impayé n'est soldé qu'à leur encaissement.
   */
  in_flight_debits: Type.Number(),
  /** Montant formaté de ces prélèvements en cours (absent sans régularisation en cours). */
  in_flight_amount: Type.Optional(Type.String()),
});
export type AdminOutstandingProObject = Static<typeof AdminOutstandingProSchema>;

/**
 * État du recouvrement d'UN pro (carte « Recouvrement » de la fiche admin). `item` est absent
 * quand le pro n'a aucune chaîne en échec ni signal d'impayé — la carte affiche alors « à jour »
 * plutôt qu'un vide ambigu.
 */
export const AdminOutstandingDetailResponseSchema = Type.Object({
  item: Type.Optional(AdminOutstandingProSchema),
});
export type AdminOutstandingDetailResponseObject = Static<typeof AdminOutstandingDetailResponseSchema>;

export const AdminOutstandingListResponseSchema = Type.Object({
  items: Type.Array(AdminOutstandingProSchema),
  total: Type.Number(),
});
export type AdminOutstandingListResponseObject = Static<typeof AdminOutstandingListResponseSchema>;

export const AdminRetryOutstandingResponseSchema = Type.Object({
  attempted: Type.Number(),
  succeeded: Type.Number(),
  failed: Type.Number(),
  /** true ssi plus aucune chaîne renewal ouverte après rejeu (dunning soldé, dégel éventuel). */
  recovered: Type.Boolean(),
  last_error_code: Type.Union([Type.String(), Type.Null()]),
  /**
   * Prélèvements SEPA du pro en cours de règlement après le rejeu (lancés par lui ou avant) :
   * ni payés ni refusés, ils bloquent la résolution du dunning jusqu'à leur issue.
   */
  pending_debits: Type.Optional(Type.Number()),
});
export type AdminRetryOutstandingResponseObject = Static<typeof AdminRetryOutstandingResponseSchema>;
